import { useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useGetPtoProjectQuery } from '@shared/api';
import { setActiveContractId, setActiveProjectId } from '@shared/lib/activeProjectContext';

const NavContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3.5)}`};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ProjectTitleLink = styled(Link)`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const MetaBadge = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const GroupsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing(2.5)};
  align-items: flex-start;
`;

const ContourGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const GroupLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.muted};
  padding-left: 4px;
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const NavPill = styled(Link)<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 12.5px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  text-decoration: none;
  white-space: nowrap;
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primarySoft : theme.colors.surface};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.text.primary};
  transition: all 140ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const CONTOURS: Array<{
  group: string;
  items: Array<{ key: string; label: string; absoluteTo?: string }>;
}> = [
  {
    group: '1. Документация и ППР',
    items: [
      { key: '', label: 'Реестр РД и Входной контроль' },
      { key: 'changes', label: 'Изменения РД и SQ' },
      { key: 'chat', label: 'Чат с РД' },
      { key: 'ppr-tk', label: 'ППР и ТК' },
      { key: 'checklists', label: 'Комплектность ИРД' },
    ],
  },
  {
    group: '2. Поле и Журналы',
    items: [
      { key: 'ozhr', label: 'Журнал работ (ОЖР)' },
      { key: 'inspections', label: 'Инспекции (СК)' },
      { key: 'requests', label: 'Заявки (Гео / МТО / Лаб)' },
    ],
  },
  {
    group: '3. Приёмка КФО и ИД',
    items: [
      { key: 'kfo', label: 'КФО и RFI' },
      { key: 'acts', label: 'Акты ИД (АОСР) и СТДО' },
      { key: 'smr-registers', label: 'Реестры СМР' },
      { key: 'exec-schemes', label: 'Исп. схемы', absoluteTo: '/agents/pto/exec-schemes' },
    ],
  },
  {
    group: '4. КС и Аналитика',
    items: [
      { key: 'ks', label: 'КС-2 из факта' },
      { key: 'reconciliation', label: 'Сверка объёмов' },
      { key: 'reports', label: 'Суточные сводки' },
    ],
  },
];

export function PtoProjectNav({ projectId }: { projectId: string }) {
  const location = useLocation();
  const { currentData } = useGetPtoProjectQuery(projectId, { skip: !projectId });
  const project = currentData?.project;

  useEffect(() => {
    if (projectId) {
      setActiveProjectId(
        projectId,
        project ? { name: project.name, code: project.code } : undefined,
      );
    }
    const cid = (project as { contract_id?: string | null } | undefined)?.contract_id;
    if (cid) {
      setActiveContractId(cid);
    }
  }, [projectId, project]);

  const basePath = `/agents/pto/${projectId}`;
  const subPath = location.pathname.startsWith(basePath)
    ? location.pathname.slice(basePath.length).replace(/^\/+/, '')
    : '';
  const activeKey = subPath.split('/')[0] || '';

  return (
    <NavContainer aria-label="Навигация по контурам проекта ПТО">
      <TopBar>
        <ProjectTitleLink to={basePath}>
          Объект: {project?.name ?? 'Загрузка проекта…'}
          {project?.code ? ` (${project.code})` : ''}
        </ProjectTitleLink>
        <MetaBadge>
          Единый контур ОСНОВА.Инженер ↔ Прораб ↔ Геодезист ↔ МТО ↔ Сметчик
        </MetaBadge>
      </TopBar>
      <GroupsRow>
        {CONTOURS.map((c) => (
          <ContourGroup key={c.group}>
            <GroupLabel>{c.group}</GroupLabel>
            <Pills>
              {c.items.map((it) => {
                const to = it.absoluteTo
                  ? `${it.absoluteTo}?project=${encodeURIComponent(projectId)}`
                  : it.key
                    ? `${basePath}/${it.key}`
                    : basePath;
                const isActive = it.absoluteTo
                  ? location.pathname.startsWith(it.absoluteTo)
                  : it.key === ''
                    ? activeKey === '' || activeKey === 'revisions'
                    : activeKey === it.key;
                return (
                  <NavPill key={it.key || 'root'} to={to} $active={isActive}>
                    {it.label}
                  </NavPill>
                );
              })}
            </Pills>
          </ContourGroup>
        ))}
      </GroupsRow>
    </NavContainer>
  );
}
